import { channelClient, RequestPolicies } from 'shared/channels';
import { createEntityClient, runQueuedMutation } from 'shared/channels/entity';
import { DomainMessagesTypes } from 'shared/domain/messages/message';
import { projectContract } from './projectContract';
import type { ProjectCreateModel, ProjectEditModel, ProjectModel } from './types/model';

/**
 * Projects as served by the service worker. `sync` downloads the list again;
 * changes resolve once the server accepted them and reject with its error
 * (`ChannelRequestError.messageId`).
 */
export const projectClient = {
  ...createEntityClient(projectContract),

  /** One project for its details view, archived ones included; rejects when there is none. */
  getOneById(id: string): Promise<ProjectModel> {
    return channelClient.request(projectContract, 'getOneById', { id }, RequestPolicies.read);
  },

  /** Downloads the list only when it is missing (e.g. after login), and the permissions. */
  downloadWhenMissing(): void {
    channelClient.notify(projectContract, 'downloadWhenMissing', undefined);
  },

  /** The selected project's GDPR clause, as HTML. */
  getGdprClause(): Promise<string> {
    return channelClient.request(projectContract, 'getGdprClause', undefined, RequestPolicies.read);
  },

  /** Resolves with the project the server created. */
  create(projectCreateModel: ProjectCreateModel): Promise<ProjectModel> {
    return runQueuedMutation(
      projectContract,
      'create',
      (uniqueId) => ({ projectCreateModel, uniqueId }),
      DomainMessagesTypes.createdEntity
    );
  },

  async edit(projectEditModel: ProjectEditModel): Promise<void> {
    await runQueuedMutation(
      projectContract,
      'edit',
      (uniqueId) => ({ projectEditModel, uniqueId }),
      DomainMessagesTypes.updatedEntity
    );
  },
};
